/**
 * Routing on the browser's own History API.
 *
 * The app kept its location in React state, which meant a screen had no address:
 * you could not share a listing, the back button left the app entirely, and a
 * refresh dropped you at the start. A URL is not a nicety — it is how the web
 * expects a page to be identifiable.
 *
 * Written by hand rather than pulled in, because this app has no build step: it
 * loads React from a CDN and transpiles JSX in the browser. A router is a hundred
 * lines of `pushState` and a `popstate` listener; adding a bundler to avoid writing
 * them would be the larger change.
 *
 * The route object keeps the shape the screens already use — `{ name, id, tab }` —
 * so this replaces how a route is stored without touching how one is read.
 */
import React from "react";

/** name → path, and back again. One table, so the two directions cannot disagree. */
const ROUTES = [
  { name: 'browse', path: '/' },
  { name: 'trades', path: '/trades' },
  { name: 'inbox', path: '/inbox' },
  { name: 'you', path: '/you' },
  { name: 'post', path: '/post' },
  /*
   * The camera. A route rather than a step inside the posting form, because it
   * takes over the screen, holds a live camera track, and has a back button that
   * has to mean "stop the camera and go back to the form" — none of which a
   * section of a form can express.
   */
  // Posting a whole inventory at once. A route of its own rather than a dialog
  // on the Selling tab: an import takes a file, an upload and an answer worth
  // reading afterwards, and none of that survives a dialog being dismissed.
  { name: 'import', path: '/import' },
  // The catalogue operator's queue. Reachable by typing the path, and that is
  // fine: the API answers 404 to anybody who is not an operator, so the address
  // is not the protection and was never meant to be.
  /*
   * Applying to be a shop, and the state of that application afterwards.
   *
   * A route rather than the dialog it replaces: a week of opening hours, what
   * the shop sells and a line about it do not fit in one, and the phone proved
   * it — the send button ended up below the bottom of the screen.
   */
  { name: 'shop', path: '/shop' },
  /*
   * The shop's own back office, and a route rather than a tab on the profile:
   * it is a different ACCOUNT's screen. Whoever is looking at it is acting as
   * the shop, their own listings and trades are not what it shows, and the way
   * back is a switch rather than a back button.
   *
   * Its section rides on `tab`, the key the router already carries, so
   * `/shop/dashboard?tab=stock` is an address somebody can be sent.
   */
  { name: 'dashboard', path: '/shop/dashboard' },
  { name: 'admin', path: '/admin' },
  { name: 'onboarding', path: '/join' },
  /*
   * Inviting, and arriving by an invitation.
   *
   * `/join/:id` carries a referral CODE where every other `:id` carries a uuid
   * or a handle, and it is `id` because that is the only path parameter this
   * router has. It cannot collide with `/join`: that spec matches the path
   * exactly, and this one only matches something after the slash.
   *
   * A draw has an address of its own because it is the thing people send each
   * other — "look what you can win" is a link, and a link that lands on the
   * invite screen makes the reader hunt for what was meant.
   */
  { name: 'invite', path: '/invite' },
  // Where the confirmation email sends people. It existed as a link long before it
  // existed as a route: the mail was correct, the app quietly fell through to
  // browse, and nothing ever called the API.
  { name: 'verify', path: '/verify' },
  // Getting back in without the password. Two routes because it is two moments
  // separated by a mailbox: `/forgot` asks for the link, `/reset` spends the
  // one the email carries. `/reset` reads its token from the query, like
  // `/verify` does.
  { name: 'forgot', path: '/forgot' },
  { name: 'reset', path: '/reset' },
  // The only route with a parameter, and the one that makes sharing work.
  { name: 'listing', path: '/listing/:id' },
  /*
   * A trader's own page. The `:id` is a handle where they have one and the uuid
   * otherwise, which is why the API accepts either: a handle is the shareable
   * form and not everybody has set one.
   */
  { name: 'trader', path: '/trader/:id' },
  { name: 'campaign', path: '/campaign/:id' },
  { name: 'invited', path: '/join/:id' },
];

/**
 * Query keys a route is allowed to carry.
 *
 * Anything else is dropped rather than round-tripped, so a stray parameter cannot
 * become part of the app's state — and a shared link carries only what it says.
 *
 * `q`, `game` and `mode` are the browse filters: a search someone found useful is
 * exactly the kind of thing they want to send to a friend, and a URL that does not
 * change when the view does cannot be shared honestly.
 */
/*
 * Every query key the app understands.
 *
 * `want`, `wantGame` and `wantProduct` carry a search from browse to the wants
 * screen, so somebody who searched for a card and found none does not type its
 * name a second time to watch for it.
 */
/*
 * `photos` is browse's "only listings with a real photo" filter, and it is `'1'`
 * or absent rather than `'true'`/`'false'`. A filter that is off is a filter
 * that is not in the address — an off switch spelled out in a shared URL reads
 * like a decision somebody made about the recipient's view, and it is not one.
 */
// `foil` is browse's foil filter: 'only' or 'none', absent meaning either.
/*
 * `to` is the person a conversation is being STARTED with, where `id` is a
 * conversation that already exists. It is here so that opening one from a
 * seller's Message button has an address of its own: without it the screen
 * rendered at `/inbox`, the list's own URL, and the back button had no change to
 * make — going back between two entries with the same URL fires no `popstate`
 * at all, which was measured rather than assumed.
 *
 * The id only. A NAME in a URL would be untrusted text rendered as a person, and
 * a reload still lands on the list rather than fetching one — which is what the
 * inbox has always said it does.
 */
/*
 * `kind` opens the import screen on one of its two exports — stock or wants —
 * so the Wants tab's own import button lands where it said it would rather than
 * on a question the person has already answered by pressing it.
 *
 * A key of its own rather than reusing `tab`: this is not a tab, it is which
 * FILE the screen is about, and the import screen's first step exists precisely
 * because those two files look identical in a downloads folder.
 */
/*
 * `sec` is the shop back office's section — today, stock, requests, the store.
 *
 * ITS OWN KEY RATHER THAN `only` OR `tab`, both of which were already taken and
 * mean something else: `only` is browse's wants filter and `tab` is the tab
 * within a screen, which the stock table itself uses. A key with two meanings
 * works until the day somebody arrives carrying the other one.
 */
/*
 * VIEW STATE LIVES IN THE LINK — the owner, 2026-10-22: "every place we have
 * tabs, filters or sorting options we should be able to maintain their state
 * through the link", for sharing and so a refresh keeps your own filters. So:
 *
 * `sort` is an ordering, browse's or the Stock table's, each screen reading it
 * against its own list and ignoring a value it does not know. `synced` is the
 * Stock table's "Synced" chip, '1' or absent, like `photos`. `view` is the
 * admin screen's section and `days` the traffic window — not `tab`, because a
 * change of `tab` remounts the screen (App.jsx keys it), and the admin screen
 * reads its reports queue on mount whatever the section.
 *
 * `card` is a picked suggestion's English name, beside a `q` that shows the
 * name it was found by ("Elfos de Llanowar"): browse, a shelf and Stock search
 * by `card` where it is there (browse.jsx's `pickCard`).
 */
const QUERY_KEYS = ['tab', 'sec', 'kind', 'id', 'to', 'q', 'card', 'game', 'language', 'mode', 'token', 'want', 'wantGame', 'wantProduct', 'only', 'photos', 'foil', 'sort', 'synced', 'view', 'days'];

/*
 * Keys that may appear more than once, and so are always read as a LIST.
 *
 * `game` was the only one and its handling was written inline twice, once in each
 * direction. Adding `language` beside it made that a second pair of copies to
 * keep in step, which is the shape `toPath` and `fromLocation` already learned to
 * avoid the hard way — the two hand-written key lists that disagreed for months.
 *
 * Repeated rather than comma-joined: `?language=zh-Hans&language=ja` needs no
 * escaping rules to read, and a value could one day contain a comma.
 */
const REPEATABLE = new Set(['game', 'language']);

/*
 * Driven by QUERY_KEYS — the SAME list fromLocation reads.
 *
 * These were two hand-written lists that disagreed. `fromLocation` read ten keys;
 * this wrote six. So `only`, `want`, `wantGame` and `wantProduct` were parsed out
 * of a URL nothing ever put them into: setting the wants filter changed the view
 * and left the address bar alone, which meant a refresh silently dropped it and
 * the link could not be shared — while the comment above `onlyWants` claimed the
 * opposite in as many words.
 *
 * The same shape as the request field that was declared on one side and not the
 * other. One list, read and written by the same loop, cannot drift again.
 */
function toPath(route) {
  const spec = ROUTES.find((r) => r.name === route.name) || ROUTES[0];
  let path = spec.path;
  const query = new URLSearchParams();
  // `listing` carries its id in the path; `inbox` identifies a thread by query,
  // because a thread is a view of the inbox rather than a separate page.
  const idInPath = path.includes(':id');
  if (idInPath) path = path.replace(':id', encodeURIComponent(route.id || ''));

  /*
   * A repeatable key has THREE states, and an empty one is not the absent one.
   *
   * Absent means "no opinion" and lets the trader's saved games seed the view.
   * `?game=` means "all games", said out loud, and beats whatever the person
   * opening the link has saved. Present with values means exactly those.
   *
   * The empty state used to be dropped here — `value === ''` skipped it, and the
   * `if (one)` inside the loop threw away an empty array just as quietly — so
   * "All games" round-tripped to an absent key and came back as the saved list.
   * The button appeared to do nothing. Unlighting the last chip did the same by a
   * second door, because that also produces an empty selection.
   *
   * The empty check therefore moves BELOW the repeatable branch: a single-valued
   * key with no value is still nothing worth carrying.
   */
  for (const key of QUERY_KEYS) {
    if (key === 'id' && idInPath) continue;
    const value = route[key];
    if (value === undefined || value === null || value === false) continue;
    if (REPEATABLE.has(key)) {
      const values = [].concat(value).filter(Boolean);
      if (values.length) for (const one of values) query.append(key, one);
      else query.set(key, '');
      continue;
    }
    if (value === '') continue;
    query.set(key, value);
  }

  const qs = query.toString();
  return path + (qs ? `?${qs}` : '');
}

function fromLocation(loc) {
  const path = loc.pathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(loc.search);
  const extra = {};
  for (const key of QUERY_KEYS) {
    if (REPEATABLE.has(key)) {
      // PRESENCE, not truthiness — the other half of the three states above.
      // `?game=` has to arrive as an empty list rather than as no key at all, or
      // "all games" is indistinguishable from never having said anything.
      if (!params.has(key)) continue;
      extra[key] = params.getAll(key).filter(Boolean);
      continue;
    }
    const value = params.get(key);
    if (value) extra[key] = value;
  }

  for (const spec of ROUTES) {
    if (spec.path.includes(':id')) {
      const prefix = spec.path.replace('/:id', '');
      if (path.startsWith(prefix + '/')) {
        const id = decodeURIComponent(path.slice(prefix.length + 1));
        if (id) return { name: spec.name, ...extra, id };
      }
    } else if (spec.path === path) {
      return { name: spec.name, ...extra };
    }
  }
  // An unknown path is browse rather than a 404 screen: every URL this app hands
  // out is one of the above, so an unknown one is a typo or a stale link, and
  // landing somewhere useful beats landing on an apology.
  return { name: 'browse' };
}

/**
 * Keeps React state and the address bar in step.
 *
 * `popstate` is the browser telling us the user moved — never the other way round,
 * so it only ever sets state. Everything else pushes or replaces.
 */
/**
 * The address a route would have, for putting in an `href`.
 *
 * THE SAME `toPath` THE ROUTER ITSELF USES, exported rather than reimplemented,
 * because an href that disagrees with where a click actually goes is worse than
 * no href at all: the status bar would promise one page and deliver another, and
 * a middle-click would open the wrong one.
 */
export function hrefFor(route) {
  return toPath(route);
}

/**
 * How many steps INSIDE the app are behind us.
 *
 * Counted here because nothing else can answer it. `window.history.length`
 * includes whatever the browser did before this app existed, and the Android
 * WebView's own `canGoBack()` does not see these steps at all — same-document
 * `pushState` entries never reach its back-forward list, which is why the system
 * back button closed the app from a screen the app had plainly navigated to.
 * See `window.cbmBack` below.
 */
let depth = 0;

/*
 * AN OVERLAY THAT BACK CLOSES (the owner, 2026-10-22, of the Stock drawer: "I
 * believe that back should close the drawer. It would be a pretty expected
 * interaction for mobile").
 *
 * One more entry, at the SAME address, marked in `history.state` with the
 * overlay's name. Back pops it, and the overlay hears the `popstate` and
 * closes; the router's own `onPop` sees the address it already has and keeps
 * the route. Counted in `depth`, so the Android app's Back (`window.cbmBack`)
 * goes back one step instead of closing the app from a first screen.
 *
 * An overlay closed any other way must take its entry back off with
 * `history.back()`, or Back would then be a press that does nothing.
 */
export function pushOverlayEntry(name) {
  window.history.pushState({ cbmOverlay: name }, '', window.location.pathname + window.location.search + window.location.hash);
  depth += 1;
}

/** Whether the entry the browser is on is the named overlay's. */
export function onOverlayEntry(name) {
  return !!window.history.state && window.history.state.cbmOverlay === name;
}

/*
 * BACK CLOSES ANY DIALOG — the owner, 2026-10-23, asked whether the drawer's
 * rule should hold for every dialog: "I believe that they should be unified,
 * yes". `useBackCloses` is that rule, written once; the shared `Dialog`
 * (chrome.jsx) calls it, so every dialog in the product gets it.
 *
 * `openOverlays` is the dialogs holding an entry, in the order they pushed
 * them, which is the order of their entries in the history. A `popstate`
 * closes every dialog above the entry the browser landed on — the top one for
 * a Back, so a confirmation opened from inside a dialog closes alone.
 *
 * A dialog closed any other way (its X, Cancel, the scrim, its own action)
 * takes its entry off on the way out, in `flushReleased`, so the next Back is
 * not a press that does nothing. Two closed in one go (a confirmation whose
 * action also closes the dialog under it) go back two steps in one.
 *
 * The ways this could go wrong, each handled where it would happen:
 *
 * - A dialog that NAVIGATES (a confirmation whose yes opens another screen).
 *   `navigate` from a dialog's entry first steps back off every dialog's
 *   entry (`stepBack`, closing them), then pushes the new page — so no
 *   dialog entry is left under it for a later Back to land on. And while any
 *   step back is on its way, `navigate` waits for it (`settling`), or the
 *   back would land on the new page and undo it.
 * - A dialog replaced by another in one commit: the new one takes over the
 *   closing one's entry rather than pushing over it, which would strand it.
 * - A dialog that cannot be dismissed right now (a confirmation busy doing
 *   what it was asked, `onClose` absent): Back is refused, as its X is, and
 *   the entry put back, so the dialog never sits on screen with none.
 * - A filter written while a dialog is open (browse's filter sheet). Written
 *   onto the dialog's entry, the page entry under it would keep the old
 *   address, and Back would close the sheet AND undo the filters. So the
 *   address is remembered (`underlay`) and written onto the entry the
 *   browser lands on when it leaves.
 * - A reload onto a dialog's entry: the mark is wiped as the page loads, so
 *   the entry reads as the page it is. Forward onto an old one finds a name
 *   (the timestamp) owned by nothing open, and closes nothing.
 */
const openOverlays = [];
let overlaySeq = 0;
const overlayLoad = Date.now().toString(36);
let flushQueued = false;
/* A step back this file started and the `popstate` it will fire: `settling`
   is waited on, `settled` resolves it, and `leaving` says the step is a
   navigation closing every dialog on its way out. */
let settling = null;
let settled = null;
let leaving = false;
let underlay = null;

if (window.history.state && String(window.history.state.cbmOverlay || '').startsWith('dialog:')) {
  window.history.replaceState({}, '', window.location.href);
}

function stepBack(steps, leave) {
  // The router's `onPop` takes one off `depth` for the one `popstate` this fires.
  depth -= steps - 1;
  leaving = leave;
  settling = new Promise((resolve) => { settled = resolve; });
  // Never a navigation held forever on a `popstate` that did not come.
  const mine = settled;
  setTimeout(() => { if (settled === mine) { settled = null; settling = null; leaving = false; mine(); } }, 1500);
  window.history.go(-steps);
}

function onOverlayPop() {
  if (underlay !== null) {
    window.history.replaceState(window.history.state, '', underlay);
    if (!(window.history.state && window.history.state.cbmOverlay)) underlay = null;
  }
  const leave = leaving;
  if (settled) { const done = settled; settled = null; settling = null; leaving = false; done(); }
  const here = window.history.state && window.history.state.cbmOverlay;
  const at = openOverlays.findIndex((o) => o.name === here);
  while (openOverlays.length - 1 > at) {
    const top = openOverlays[openOverlays.length - 1];
    if (!top.released && !leave && !top.closable()) { pushOverlayEntry(top.name); break; }
    openOverlays.pop();
    if (!top.released) top.close();
  }
  if (!flushQueued && openOverlays.some((o) => o.released)) { flushQueued = true; queueMicrotask(flushReleased); }
}
// Registered before any screen's own listener, so the address is fixed first.
window.addEventListener('popstate', onOverlayPop);

function flushReleased() {
  flushQueued = false;
  // A step already on its way takes these with it; its `popstate` asks again.
  if (settling) return;
  const here = window.history.state && window.history.state.cbmOverlay;
  let at = openOverlays.findIndex((o) => o.name === here);
  let steps = 0;
  while (at >= 0 && openOverlays[at].released) { steps += 1; at -= 1; }
  for (let i = openOverlays.length - 1; i >= 0; i -= 1) if (openOverlays[i].released) openOverlays.splice(i, 1);
  if (steps) stepBack(steps, false);
}

/**
 * Back closes this while `open` — see the block above. `onClose` is read when
 * Back is pressed, so a caller may hand a new function every render. A dialog
 * with no `onClose` cannot be dismissed, and so pushes nothing.
 */
export function useBackCloses(open, onClose) {
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  /* A layout effect: its cleanup runs in the commit that closes the dialog,
     not some frames later, so the step back cannot overtake a navigation. */
  React.useLayoutEffect(() => {
    if (!open || !closeRef.current) return undefined;
    overlaySeq += 1;
    const entry = {
      name: `dialog:${overlayLoad}:${overlaySeq}`,
      released: false,
      closable: () => !!closeRef.current,
      close: () => { if (closeRef.current) closeRef.current(); },
    };
    const top = openOverlays[openOverlays.length - 1];
    if (top && top.released && onOverlayEntry(top.name)) {
      // Replacing a dialog closed in this same commit: its entry becomes this one's.
      openOverlays.pop();
      window.history.replaceState({ cbmOverlay: entry.name }, '', window.location.pathname + window.location.search + window.location.hash);
    } else {
      pushOverlayEntry(entry.name);
    }
    openOverlays.push(entry);
    return () => {
      if (!openOverlays.includes(entry)) return;
      entry.released = true;
      if (!flushQueued) { flushQueued = true; queueMicrotask(flushReleased); }
    };
  }, [open]);
}

export function useRouter() {
  const [route, setRoute] = React.useState(() => fromLocation(window.location));

  React.useEffect(() => {
    const onPop = () => {
      depth = Math.max(0, depth - 1);
      /*
       * A pop to an entry with the address already on screen — an overlay's
       * entry (see `pushOverlayEntry`) and the page under it share one —
       * keeps the route it has rather than handing every screen a new object
       * that says the same thing.
       */
      const here = window.location.pathname + window.location.search;
      setRoute((current) => (current && toPath(current) === here ? current : fromLocation(window.location)));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /*
   * THE HOST ASKS THE PAGE WHETHER BACK MEANS ANYTHING HERE.
   *
   * The Android shell used to decide alone, with `WebView.canGoBack()`, and that
   * question has the wrong answer for a single-page app: it reports the pages it
   * LOADED, and this app loads one and then pushes state. So it said "nowhere to
   * go" from the fifth screen in and the app closed.
   *
   * Returning true means the app consumed the press and has already gone back.
   * False means there is nothing of ours left, and closing is right.
   */
  React.useEffect(() => {
    window.cbmBack = () => {
      if (depth <= 0) return false;
      window.history.back();
      return true;
    };
    return () => { delete window.cbmBack; };
  }, []);

  const cameFrom = React.useRef(null);

  const navigate = React.useCallback(function go(name, params = {}, options = {}) {
    // A dialog's step back is still on its way: after it, or it undoes this.
    if (settling) { settling.then(() => go(name, params, options)); return; }
    const next = { name, ...params };
    const url = toPath(next);
    const overlay = window.history.state && window.history.state.cbmOverlay;
    const dialogAt = openOverlays.findIndex((o) => o.name === overlay);
    // Replacing is for routes that should not be somewhere you can go BACK to —
    // a guard sending you to sign in, for instance.
    if (options.replace) {
      // On an overlay's entry, it keeps its mark, and the page under it gets the address on the way out.
      if (overlay) { window.history.replaceState(window.history.state, '', url); underlay = url; }
      else window.history.replaceState({}, '', url);
    } else if (dialogAt >= 0) {
      // Leaving from inside a dialog: off every dialog's entry first, then the push (see `useBackCloses`).
      stepBack(dialogAt + 1, true);
      settling.then(() => go(name, params, options));
      return;
    } else if (url !== window.location.pathname + window.location.search) {
      window.history.pushState({}, '', url);
      depth += 1;
    }
    /*
     * The functional form, to read the screen being LEFT without holding a
     * stale `route` in this callback's closure — it is built once, with no
     * dependencies, precisely so navigating never re-creates it.
     *
     * `cameFrom` is what lets the store decide that going BACK after a sign-in
     * would return somebody to a page they must never see again. See
     * ONE_SHOT_ROUTES there.
     */
    setRoute((current) => { cameFrom.current = current?.name ?? null; return next; });
    /*
     * `keepScroll` for a control writing its own state into the link — a
     * search box half way down a page, typed into, must not throw the page to
     * the top on every keystroke. A new screen still starts at the top.
     */
    if (!options.keepScroll) window.scrollTo(0, 0);
  }, []);

  const back = React.useCallback(function leave() {
    if (settling) { settling.then(leave); return; }
    // Ask the browser, so its history and ours can never disagree. If there is
    // nowhere to go back to — someone opened a shared listing directly — send them
    // to browse rather than out of the app.
    if (window.history.length > 1) window.history.back();
    else navigate('browse', {}, { replace: true });
  }, [navigate]);

  /*
   * The screen the last navigation left.
   *
   * A ref rather than state: nothing renders from it, and making it state would
   * re-render every screen on every navigation to tell them something only one
   * decision cares about.
   */
  return { route, navigate, back, cameFrom };
}

